import { takeLatest, put, call, takeEvery } from 'redux-saga/effects';
import {
    setLoading,
    setSuccess,
    setClassList,
    setClassCreation,
    clearClassData,
    setListClassModule,
    setDistrictList,
    setLoadingDistrict,
    setLoadingProvince,
    setLoadingWard,
    setProvinceList,
    setWardList,
    setSchoolList,
    setLoadingSchool,
    setClassModuleList,
    setAddLoading,
    setRemoveLoading,
    setLoadingDownloadTemplateClass,
    setTemplateClassFile,
    clearTemplateClassFile,
    setSchoolCountList,
    setLoadingDownloadImportClassTemplate,
    setImportClassTemplateFile,
    setLoadingImportClassList,
    setImportedClassListPreview,
    setImportClassListSuccess,
    setClassSchoolModuleOptions,
    setLoadingClassSchoolModules,
    setLoadingDeleteClass,
    setDeleteClassSuccess,
    setLoadingFetchClassModules,
} from '@store/reducers/classesReducer';
import {
    fetchingClassList,
    createClass,
    getClassDetail,
    updateClass,
    clearClass,
    fetchListClassModules,
    fetchListClassModulesEvery,
    fetchClassDistrictList,
    fetchProviceList,
    fetchWardList,
    fetchSchoolList,
    fetchClassModuleList,
    addClassModule,
    removeClassModule,
    addClassStudents,
    removeClassStudents,
    downloadTemplateAccountClassList,
    downloadTemplateClassImport,
    importClassList,
    fetchClassSchoolModules,
    deleteClass,
} from '@store/actions/classesActions';
import { toast } from 'react-toastify';
import { history } from '@utils/BrowserHistory';
import { fileService, resourceService } from '@libs/axios';
import { ResponseType } from '@store/index';

const API_URL = process.env.REACT_APP_API_URL;

function mapClassApiToCreation(data: any) {
    return {
        name: data?.name,
        code: data?.code,
        student_count: data?.student_count,
        school: data?.school,
        school_year: data?.school_year,
        head_teacher_name: data?.head_teacher_name,
        grade: data?.grade,
        status: data?.status,
        class_type: data?.class_type,
        partner: data?.partner ?? null,
        users: data?.users,
    };
}

function normalizeClassModulesResponse(data: any): any[] {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    return [];
}

function navigateToClassListWithSavedFilters() {
    try {
        const savedFilters = localStorage.getItem('classListFilters');
        if (savedFilters) {
            const filters = JSON.parse(savedFilters);
            const queryString = Object.entries(filters)
                .filter(([_, value]) => value !== null && value !== '')
                .map(([key, value]) => `${key}=${value}`)
                .join('&');
            history.push(`/admin/class${queryString ? `?${queryString}` : ''}`);
        } else {
            history.push('/admin/class');
        }
    } catch {
        history.push('/admin/class');
    }
}

function* fetchClassListData(action: ReturnType<typeof fetchingClassList>) {
    const { code_name, province, district, ward, school, school_year, status, page, page_size, newProvince, newWard, schoolType, is_current_school_year, class_type, partner } = action.payload;
    yield put(setLoading(true));
    try {
        if (school === 'undefined') {
            yield put(setClassList([]));
            yield put(setLoading(false));
            return;
        }
        const response: ResponseType = yield call(
            resourceService.get,
            API_URL + '/api/classes/', {
            params: {
                code_name,
                province,
                district,
                ward,
                ward2:newWard,
                province2:newProvince,
                school_type:schoolType,
                school,
                school_year,
                status,
                page: page ?? 1,
                page_size: page_size ?? 150,
                is_current_school_year,
                class_type,
                ...(partner !== null && partner !== undefined && partner !== '' ? { partner } : {}),
            }
        }
        );

        if (response?.data) {
            yield put(setClassList(response.data));
            yield put(setLoading(false));
        }
    } catch (e: any) {
        yield put(setLoading(false));
        let error: Error = e;
        console.log(error);
    }
}

function* createClassSaga(action: ReturnType<typeof createClass>) {
    yield put(setSuccess(false));
    yield put(setLoading(true));
    try {
        const response: ResponseType = yield call(resourceService.post, API_URL + '/api/classes/', {
            ...action.payload,
            school_year: new Date().getFullYear(),
            code: 'LH',
            school_id: action.payload.school,
            grade_id: action.payload.grade,
            class_type: action.payload.class_type,
            partner: action.payload.partner || null,
        });
        if (response.status == 201) {
            toast.success('Tạo lớp học mới thành công');
            yield put(setSuccess(true));
            yield put(setLoading(false));
        } else {
            yield put(setSuccess(false));
            yield put(setLoading(false));
        }
    } catch (error: any) {
    const detail = error?.response?.data?.detail;
    let errorMessage = error?.message || 'An error occurred';

    // Xử lý lỗi trùng tên cụ thể
    if (detail === "A class with same name and school has already existed") {
        errorMessage = "Lớp học đã tồn tại trên hệ thống.";
    } else if (detail) {
        errorMessage = detail;
    }

    console.log(errorMessage);
    
    toast.error(`Tạo lớp học mới thất bại: ${errorMessage}`);
    yield put(setSuccess(false));
    yield put(setLoading(false));
}
}

function* getClassDetailSaga(action: any) {
    const { id } = action.payload;
    yield put(setLoading(true));
    try {
        const response: ResponseType = yield call(resourceService.get, API_URL + `/api/classes/${id}/`);
        if (response?.data) {
            yield put(setClassCreation(mapClassApiToCreation(response.data)));
            yield put(setLoading(false));
        }
    } catch (error: any) {
        yield put(setLoading(false));
        console.log(error);
    }
}

function* clearClassSaga(action: any) {
    yield put(clearClassData());
}

function* updateClassSaga(action: any): Generator<any, void, any> {
    yield put(setSuccess(false));
    yield put(setLoading(true));
    try {
        const { data, id } = action.payload;
        const skipRedirect = data?.__skipRedirect === true;
        const skipNavigation = data?.__skipNavigation === true;
        const {
            school_year,
            __skipRedirect: _r,
            __skipNavigation: _n,
            school,
            grade,
            class_type,
            module_ids,
            ...rest
        } = data;
        const updatedData: Record<string, any> = { ...rest };
        if (school !== undefined && school !== null && school !== '') {
            updatedData.school_id = school;
        }
        if (grade !== undefined && grade !== null && grade !== '') {
            updatedData.grade_id = grade;
        }
        if (class_type !== undefined) {
            updatedData.class_type = class_type;
        }
        if (module_ids !== undefined) {
            updatedData.module_ids = module_ids;
        }

        const response: ResponseType = yield call(resourceService.put, API_URL + `/api/classes/${id}/`, updatedData);
        if (response.status == 200) {
            toast.success(
                skipRedirect
                    ? 'Cập nhật môn học lớp thành công'
                    : 'Chỉnh sửa thông tin lớp học thành công',
            );
            yield put(setSuccess(true));
            yield put(setLoading(false));
            if (skipRedirect) {
                // Module-assign only: refresh class modules. Avoid setClassCreation (would
                // retrigger ClassGeneralInfo → api/schools) and avoid refetching school_module
                // options here (modal refetches when opened).
                yield put(fetchClassModuleList(String(id)));
            } else {
                yield put(setClassCreation(mapClassApiToCreation(response.data)));
                if (!skipNavigation) {
                    history.push(`/admin/classdetail/?mode=view&id=${response.data.id}`);
                }
            }
        } else {
            yield put(setSuccess(false));
            yield put(setLoading(false));
        }
    } catch (error: any) {
    const detail = error?.response?.data?.detail;
    let errorMessage = error?.message || 'An error occurred';

    // Xử lý lỗi trùng tên
    if (detail === "A class with same name and school has already existed") {
        errorMessage = "Lớp học đã tồn tại trên hệ thống.";
    } else if (detail) {
        errorMessage = detail;
    }

    console.log(errorMessage);

    toast.error(`Chỉnh sửa thông tin lớp học thất bại: ${errorMessage}`);
    yield put(setSuccess(false));
    yield put(setLoading(false));
}
}

function* fetchListModuleClassSaga(
    action: any,
) {
    yield put(setLoading(true));
    try {
        const { pageSize, page, status, classId } = action.payload;
        let response : ResponseType;
        if (status !== undefined && status !== null) {
            response = yield call(
                resourceService.get,
                API_URL + `/api/classes/${classId}/modules/?status=${status}`,
            );
        } else {
            response = yield call(
                resourceService.get,
                API_URL + `/api/classes/${classId}/modules/`,
            );
        }
        if (response.status == 200) {
            yield put(setLoading(false));
            yield put(setListClassModule(response?.data));
        }
    } catch (error: any) {
        yield put(setLoading(false));
    }
}

function* fetchListModuleClassEverySaga(
    action: any,
): Generator<any, void, any> {
    yield put(setLoading(true));
    try {
        const { pageSize, page, status, classId } = action.payload;
        const response: ResponseType = yield call(
            resourceService.get,
            API_URL + `/api/classes/${classId}/modules/`,
        );
        if (response.status == 200) {
            yield put(setLoading(false));
            yield put(setListClassModule(response?.data));
        }
    } catch (error: any) {
        yield put(setLoading(false));
    }
}

function* fetchProviceListSaga(action: any) {
    try {
        yield put(setLoadingProvince(true));
        const response: ResponseType = yield call(
            resourceService.get,
            API_URL + `/api/provinces/?page=1&page_size=100`,
        );
        if (response.status === 200) {
            yield put(setProvinceList(response.data?.results));
            yield put(setLoadingProvince(false));
        }
    } catch (error: any) {
        yield put(setLoadingProvince(false));
        console.log(error);
    }
}

function* fetchDistricListSaga(action: any) {
    try {
        const { provinceId } = action.payload;
        yield put(setLoadingDistrict(true));
        const response: ResponseType = yield call(
            resourceService.get,
            API_URL + `/api/districts/?province=${provinceId}&page_size=500`,
        );
        if (response.status === 200) {
            yield put(setDistrictList(response.data?.results));
            yield put(setLoadingDistrict(false));
        }
    } catch (error: any) {
        yield put(setLoadingDistrict(false));
        console.log(error);
    }
}

function* fetchWardListSaga(action: any) {
    try {
        yield put(setLoadingWard(true));
        const { districtId } = action.payload;
        const response: ResponseType = yield call(
            resourceService.get,
            API_URL + `/api/wards?district=${districtId}&page_size=500`,
        );
        if (response.status === 200) {
            yield put(setWardList(response.data?.results));
            yield put(setLoadingWard(false));
        }
    } catch (error: any) {
        yield put(setLoadingWard(false));
        console.log(error);
    }
}

function* fetchSchoolListSaga(action: ReturnType<typeof fetchSchoolList>) {
    const { wardId, districtId, provinceId, newProvinceId, newWardId, search, page, page_size } = action.payload;
    const params: any = {
        page, page_size, status: 1
    };

    if (provinceId !== null) {
        params.province = provinceId;
    }
    if (districtId !== null) {
        params.district = districtId;
    }
    if (wardId !== null) {
        params.ward = wardId;
    }
    if (newWardId !== null) {
        params.ward2 = newWardId;
    }
    if (newProvinceId !== null) {
        params.province2 = newProvinceId;
    }
    if (search !== '') {
        params.name = search;
    }

    try {
        yield put(setLoadingSchool(true));
        const response: ResponseType = yield call(
            resourceService.get,
            API_URL + `/api/schools/`, { params }
        );
        if (response.status === 200) {
            yield put(setSchoolList(response.data?.results));
            yield put(setSchoolCountList(response.data?.count));
            yield put(setLoadingSchool(false));
        }
    } catch (error: any) {
        yield put(setLoadingSchool(false));
        console.log(error);
    }
}

function* fetchClassModuleListSaga(action: any) {
    const { classId } = action.payload;
    yield put(setLoadingFetchClassModules(true));
    try {
        const response: ResponseType = yield call(
            resourceService.get,
            API_URL + `/api/classes/${classId}/modules/`,
        );
        if (response.status === 200) {
            yield put(setClassModuleList(normalizeClassModulesResponse(response.data)));
        }
    } catch (error: any) {
        console.error(error);
    } finally {
        yield put(setLoadingFetchClassModules(false));
    }
}

function* addClassModuleSaga(action: any) {
    const { moduleIds, classId } = action.payload;
    yield put(setLoading(true));
    try {
        const response: ResponseType = yield call(resourceService.post, API_URL + `/api/classes/${classId}/add_class_module/`, { module_id: moduleIds })
        if (response.status === 201) {
            const response: ResponseType = yield call(
                resourceService.get,
                API_URL + `/api/classes/${classId}/modules/`,
            );
            if (response.status === 200) {
                yield put(setClassModuleList(normalizeClassModulesResponse(response.data)));
                yield put(setLoading(false));
                toast.success("Đăng ký môn học thành công !");
            }
        }
    }
    catch (error: any) {
        const detailError = error?.response?.data?.detail || 'Có lỗi xảy ra';
        toast.error(`Đăng ký môn học thất bại: ${detailError}`);
        yield put(setLoading(false));
        console.error(error);
    }
}

function* removeClassModuleSaga(action: any) {
    const { moduleIds, classId } = action.payload;
    yield put(setLoading(true));
    try {
        const response: ResponseType = yield call(resourceService.delete, API_URL + `/api/classes/${classId}/remove_class_module/`, { data: { module_id: moduleIds } });

        if (response.status === 204 || (response.status === 207)) {
            const response: ResponseType = yield call(
                resourceService.get,
                API_URL + `/api/classes/${classId}/modules/`,
            );
            if (response.status === 200) {
                yield put(setClassModuleList(normalizeClassModulesResponse(response.data)));
                yield put(setLoading(false));
                toast.success("Xóa Module thành công!");
            }
        }
    }
    catch (error: any) {
        const detailError = error?.response?.data?.detail || 'Có lỗi xảy ra';
        toast.error(`Xóa Module thất bại: ${detailError}`);
        yield put(setLoading(false));
        console.error(error);
    }
}

function* addClassStudentsSaga(action: any) {
    const { studentIds, classId } = action.payload;
    yield put(setAddLoading(true));
    try {
        const response: ResponseType = yield call(resourceService.post, API_URL + `/api/classes/${classId}/add_students/`, { user_id: studentIds })
        if (response.status === 201) {
            toast.success("Thêm học sinh thành công!");
            yield put(setAddLoading(false));
        } else {
            yield put(setAddLoading(false));
            const detailError = response.data?.errors || 'Có lỗi xảy ra';
            toast.error(`Thêm học sinh thất bại: ${detailError}`);
        }
    }
    catch (error: any) {
        const detailError = error.response?.data?.detail || 'Có lỗi xảy ra';
        toast.error(`Thêm học sinh thất bại: ${detailError}`);
        yield put(setAddLoading(false));
        console.error(error);
    }
}

function* removeClassStudentsSaga(action: any) {
    const { studentIds, classId } = action.payload;
    yield put(setRemoveLoading(true));
    try {
        const response: ResponseType = yield call(resourceService.delete, API_URL + `/api/classes/${classId}/remove_students/`, { data: { user_id: studentIds } });

        if (response.status === 200) {
            toast.success("Xóa học sinh thành công!");
            yield put(setRemoveLoading(false));
        } else {
            yield put(setRemoveLoading(false));
            const detailError = response.data?.errors || 'Có lỗi xảy ra';
            toast.error(`Xóa học sinh thất bại: ${detailError}`);
        }
    }
    catch (error: any) {
        const detailError = error.response?.data?.detail || 'Có lỗi xảy ra';
        toast.error(`Xóa học sinh thất bại: ${detailError}`);
        yield put(setRemoveLoading(false));
        console.error(error);
    }
}

function* downloadTemplateAccountClassListSaga(action: ReturnType<typeof downloadTemplateAccountClassList>) {
    yield put(setLoadingDownloadTemplateClass(true));
    const { classId, schoolId } = action.payload;
    try {
        const response: ResponseType = yield call(
            fileService.get,
            API_URL + `/api/download-template-class-account-excel/?class=${classId}&school=${schoolId}`,
            {
                responseType: 'blob' // Set response type to blob for binary data
            }
        );
        if (response.status === 200) {
            yield put(setTemplateClassFile(response.data));
        } else {
            toast.error('Tải file template thất bại: ' + response.data.error);
        }
    } catch (error: any) {
        console.log(error);
        toast.error('Tải file template thất bại: ' + (error.response?.data?.error || 'Lỗi không xác định'));
    } finally {
        yield put(setLoadingDownloadTemplateClass(false));
    }
}

function* downloadTemplateClassImportSaga() {
    yield put(setLoadingDownloadImportClassTemplate(true));
    try {
        const response: ResponseType = yield call(
            fileService.get,
            API_URL + '/api/dowload_template_class/',
            {
                responseType: 'blob' // BE now returns the .xlsx file directly, not {file_url}
            }
        );
        if (response.status === 200) {
            yield put(setImportClassTemplateFile(response.data));
        } else {
            toast.error('Tải file import mẫu thất bại');
        }
    } catch (error: any) {
        console.log(error);
        toast.error('Tải file import mẫu thất bại: ' + (error.response?.data?.error || 'Lỗi không xác định'));
    } finally {
        yield put(setLoadingDownloadImportClassTemplate(false));
    }
}

function* importClassListSaga(action: ReturnType<typeof importClassList>) {
    yield put(setLoadingImportClassList(true));
    yield put(setImportClassListSuccess(false));
    const { file } = action.payload;
    try {
        const formData = new FormData();
        formData.append('file', file);
        const response: ResponseType = yield call(
            resourceService.post,
            API_URL + '/api/import_class/',
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }
        );
        if (response.status === 200) {
            const previewList = response?.data?.class_import || [];
            yield put(setImportedClassListPreview(previewList));
            yield put(setImportClassListSuccess(true));
            toast.success('Import lớp học thành công');
        } else {
            const errorMessage = response?.data?.detail || 'Import lớp học thất bại, vui lòng thử lại';
            toast.error(errorMessage);
        }
    } catch (error: any) {
        const rawErrorMessage = error?.response?.data?.detail || '';
        const errorMessage = typeof rawErrorMessage === 'string' && rawErrorMessage.includes('not a zip file')
            ? 'File import không đúng định dạng .xlsx. Vui lòng tải lại file mẫu và import lại.'
            : (rawErrorMessage || 'Import lớp học thất bại, vui lòng thử lại');
        toast.error(errorMessage);
    } finally {
        yield put(setLoadingImportClassList(false));
    }
}

function* deleteClassSaga(
    action: ReturnType<typeof deleteClass>,
): Generator<any, void, any> {
    const { id } = action.payload;
    yield put(setLoadingDeleteClass(true));
    try {
        const response: ResponseType = yield call(
            resourceService.delete,
            API_URL + `/api/classes/${id}/`,
        );
        if (response.status === 204 || response.status === 200) {
            toast.success('Đã xóa lớp học thành công.');
            yield put(setDeleteClassSuccess(true));
        }
    } catch (error: any) {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        if (status === 400) {
            if (detail === 'Cannot delete class with associated students.') {
                toast.error(
                    'Không thể xóa lớp vì lớp vẫn còn học sinh hoặc người dùng liên kết.',
                );
            } else {
                toast.error(
                    typeof detail === 'string' && detail.trim()
                        ? detail
                        : 'Yêu cầu không hợp lệ.',
                );
            }
        } else if (status === 403) {
            toast.error('Bạn không có quyền thực hiện thao tác này.');
        } else if (status !== 401) {
            const msg =
                error?.response?.data?.message ||
                error?.message ||
                'Xóa lớp học thất bại';
            toast.error(typeof msg === 'string' ? msg : 'Xóa lớp học thất bại');
        }
        console.error(error);
    } finally {
        yield put(setLoadingDeleteClass(false));
    }
}

function* fetchClassSchoolModulesSaga(
    action: ReturnType<typeof fetchClassSchoolModules>,
): Generator<any, void, any> {
    const { classId } = action.payload;
    yield put(setLoadingClassSchoolModules(true));
    try {
        const response: ResponseType = yield call(
            resourceService.get,
            API_URL + '/api/school_module/',
            { params: { class_id: classId } },
        );
        if (response.status === 200) {
            yield put(
                setClassSchoolModuleOptions(
                    Array.isArray(response.data) ? response.data : [],
                ),
            );
        } else {
            yield put(setClassSchoolModuleOptions([]));
        }
    } catch (e) {
        yield put(setClassSchoolModuleOptions([]));
        console.error(e);
    } finally {
        yield put(setLoadingClassSchoolModules(false));
    }
}

function* classSaga(): Generator<any, void, any> {
    yield takeLatest(fetchingClassList, fetchClassListData);
    yield takeLatest(createClass, createClassSaga);
    yield takeLatest(getClassDetail, getClassDetailSaga);
    yield takeLatest(clearClass, clearClassSaga);
    yield takeLatest(fetchListClassModules as any, fetchListModuleClassSaga);
    yield takeEvery(fetchListClassModulesEvery as any, fetchListModuleClassEverySaga);
    yield takeLatest(fetchProviceList, fetchProviceListSaga);
    yield takeLatest(fetchClassDistrictList, fetchDistricListSaga);
    yield takeLatest(fetchWardList, fetchWardListSaga);
    yield takeLatest(fetchSchoolList, fetchSchoolListSaga);
    yield takeLatest(updateClass as any, updateClassSaga);
    yield takeLatest(fetchClassModuleList, fetchClassModuleListSaga);
    yield takeLatest(addClassModule, addClassModuleSaga);
    yield takeLatest(removeClassModule, removeClassModuleSaga);
    yield takeLatest(addClassStudents, addClassStudentsSaga);
    yield takeLatest(removeClassStudents, removeClassStudentsSaga);
    yield takeLatest(downloadTemplateAccountClassList, downloadTemplateAccountClassListSaga);
    yield takeLatest(downloadTemplateClassImport, downloadTemplateClassImportSaga);
    yield takeLatest(importClassList, importClassListSaga);
    yield takeLatest(fetchClassSchoolModules, fetchClassSchoolModulesSaga);
    yield takeLatest(deleteClass.type, deleteClassSaga);
}

export default classSaga;
